import React, { useState } from "react";
import { InputNumber } from "antd";
import type { BufferedNumberInputProps } from "./types";

function clampValue(value: number, min?: number, max?: number): number {
  let nextValue = value;

  if (typeof min === "number") {
    nextValue = Math.max(min, nextValue);
  }

  if (typeof max === "number") {
    nextValue = Math.min(max, nextValue);
  }

  return nextValue;
}

function toNearestInteger(value: number): number {
  return Math.round(value);
}

function BufferedNumberInput({
  value,
  onCommit,
  disabled = false,
  fallback,
  min = undefined,
  max = undefined,
  step = undefined,
  size = "small",
  style = undefined,
  placeholder = undefined,
}: BufferedNumberInputProps) {
  const [draftValue, setDraftValue] = useState<number | null | undefined>(undefined);

  let resolvedValue: number | null = null;
  if (draftValue === undefined) {
    resolvedValue = typeof value === "number" ? toNearestInteger(value) : null;
  } else {
    resolvedValue = typeof draftValue === "number" ? toNearestInteger(draftValue) : null;
  }

  const commitValue = () => {
    if (draftValue === undefined) {
      return;
    }

    let baseValue = fallback;
    if (typeof draftValue === "number") {
      baseValue = draftValue;
    } else if (typeof value === "number") {
      baseValue = value;
    }

    const normalizedValue = toNearestInteger(clampValue(baseValue, min, max));

    onCommit(normalizedValue);
    setDraftValue(undefined);
  };

  return (
    <InputNumber
      size={size}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      precision={0}
      value={resolvedValue}
      onFocus={() => {
        setDraftValue(typeof value === "number" ? toNearestInteger(value) : null);
      }}
      onChange={(nextValue) => {
        setDraftValue(typeof nextValue === "number" ? toNearestInteger(nextValue) : null);
      }}
      onBlur={commitValue}
      onPressEnter={commitValue}
      placeholder={placeholder}
      style={style}
    />
  );
}

export default BufferedNumberInput;
